import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@altimateai/lego";
import {
  createTestCase,
  updateTestCase,
  deleteTestCase,
  deleteEvalResult,
  runEval,
  createCriterion,
  updateCriterion,
  deleteCriterion,
  CreateTestCaseRequest,
  CreateCriterionRequest,
  TestCase,
} from "../../services/evals";

export const useEvalMutations = () => {
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: createTestCase,
    onSuccess: newTestCase => {
      if (!newTestCase) {
        queryClient.invalidateQueries({ queryKey: ["testCases"] });
        toast({ description: "Test case created successfully" });
        return;
      }
      // Manually add the new test case to the cache (instant update!)
      queryClient.setQueryData<TestCase[]>(["testCases"], oldData => {
        if (!oldData) return [newTestCase];
        return [newTestCase, ...oldData];
      });

      toast({ description: "Test case created successfully" });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      toast({ description: `Failed to create test case: ${message}`, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateTestCaseRequest }) =>
      updateTestCase(id, data),
    onSuccess: (updatedTestCase, variables) => {
      if (!updatedTestCase) {
        queryClient.invalidateQueries({ queryKey: ["testCases"] });
        toast({ description: "Test case updated successfully" });
        return;
      }
      // Manually update just this one test case in the cache (instant update!)
      queryClient.setQueryData<TestCase[]>(["testCases"], oldData => {
        if (!oldData) return oldData;
        return oldData.map(tc => (tc.id === variables.id ? updatedTestCase : tc));
      });

      toast({ description: "Test case updated successfully" });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      toast({ description: `Failed to update test case: ${message}`, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTestCase,
    onSuccess: (_, deletedId) => {
      // Manually remove the deleted test case from the cache (instant update!)
      queryClient.setQueryData<TestCase[]>(["testCases"], oldData => {
        if (!oldData) return oldData;

        // Filter out the deleted test case
        return oldData.filter(tc => tc.id !== deletedId);
      });

      toast({ description: "Test case deleted successfully" });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      toast({ description: `Failed to delete test case: ${message}`, variant: "destructive" });
    },
  });

  const deleteResultMutation = useMutation({
    mutationFn: deleteEvalResult,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["evalResults"] });
      toast({ description: "Result deleted successfully" });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      toast({ description: `Failed to delete result: ${message}`, variant: "destructive" });
    },
  });

  const runEvalMutation = useMutation({
    mutationFn: runEval,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["evalResults"] });
      toast({ description: "Evaluation completed successfully" });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      toast({ description: `Failed to run evaluation: ${message}`, variant: "destructive" });
    },
  });

  const createCriterionMutation = useMutation({
    mutationFn: createCriterion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["criteria"] });
      toast({ description: "Criterion created successfully" });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      toast({ description: `Failed to create criterion: ${message}`, variant: "destructive" });
    },
  });

  const updateCriterionMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateCriterionRequest }) =>
      updateCriterion(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["criteria"] });
      toast({ description: "Criterion updated successfully" });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      toast({ description: `Failed to update criterion: ${message}`, variant: "destructive" });
    },
  });

  const deleteCriterionMutation = useMutation({
    mutationFn: deleteCriterion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["criteria"] });
      toast({ description: "Criterion deleted successfully" });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      toast({ description: `Failed to delete criterion: ${message}`, variant: "destructive" });
    },
  });

  return {
    createMutation,
    updateMutation,
    deleteMutation,
    deleteResultMutation,
    runEvalMutation,
    createCriterionMutation,
    updateCriterionMutation,
    deleteCriterionMutation,
  };
};
